import React, { useEffect, useState, useMemo, useRef } from 'react'
import Link from '../../../Routing/Link'
import Button from '../../../../widgets/Button/Base'
import styles from './Dropdown.module.css'
import DirectionAware from '../../../../widgets/Layout/DirectionAware/DirectionAware'
import ArrowIcon from '../../../../legacy_components/Icons/ArrowIcon'
import ExternalLink from '../../../Routing/ExternalLink'
import useClick from '../../../../hooks/useClick'
import useEscapePress from '../../../../hooks/useEscapePress'
import useTabPress from '../../../../hooks/useTabPress'
import translate from '../../../../utilities/translate'
import translations from './translations'
import uniqid from 'uniqid'
import { getLocales } from '../../../../utilities/locales'
import PropTypes from 'prop-types'

const trans = translate(translations)

const Dropdown = ({ locale, children }) => {
  const ref = useRef(null)
  const menuId = useMemo(() => uniqid(), [children])
  const [isMenuOpen, setIsMenuOpen] = useState(false)

  useEffect(() => {
    // In order to close other opened header dropdown
    if (isMenuOpen) {
      Array
        .from(document.querySelectorAll(`.${styles.opened}`))
        .filter(dropdown => dropdown != ref.current)
        .map(dropdown => dropdown.querySelector(`.${styles['toggle-wrapper']} > button`)?.click())
    }
  }, [isMenuOpen])

  useEscapePress(() => setIsMenuOpen(false))
  useTabPress(event => {
    if (ref.current && !ref.current.contains(event.target)) {
      setIsMenuOpen(false)
    }
  })

  return (
    <div className={ `${styles.wrapper} ${isMenuOpen ? styles.opened : ''}` } ref={ ref }>
      { React.Children.map(children, child => React.cloneElement(
        child,
        { ...child.props, locale, menuId, isMenuOpen, setIsMenuOpen },
      )) }
    </div>
  )
}

const Toggle = ({
  locale,
  menuId,
  isMenuOpen,
  setIsMenuOpen,
  children,
  buttonId = undefined,
  interactiveChild = false,
}) =>
  <div className={ styles['toggle-wrapper'] } aria-controls={ menuId }>
    { interactiveChild
      ? <span className={ styles.toggle }>{ children }</span>
      : null
    }
    <Button
      id={ buttonId }
      aria-expanded={ isMenuOpen }
      aria-label={ trans(locale)('toggle') }
      onClick={ () => setIsMenuOpen(!isMenuOpen) }
    >
      <span className={ styles.toggle }>
        { !interactiveChild ? children : null }
        <DirectionAware direction="bottom">
          <ArrowIcon width={ 5 } aria-label={ trans(locale)('toggleIcon') } />
        </DirectionAware>
      </span>
    </Button>
  </div>

const Menu = ({ menuId, setIsMenuOpen, children }) => {
  const ref = useRef(null)

  // To manage outside click on mobile view
  useClick(event => {
    if (ref.current && !ref.current.contains(event.target)) {
      setIsMenuOpen(false)
    }
  })

  return (
    <div className={ styles['menu-wrapper'] }>
      <ul ref={ ref } id={ menuId } className={ styles.menu }>
        { React.Children.map(children, child => React.cloneElement(
          child,
          { ...child.props, setIsMenuOpen },
        )) }
      </ul>
    </div>
  )
}

const ItemLink = ({ setIsMenuOpen, href, children, isActive }) =>
  <li
    className={ `${styles.item} ${isActive ? styles.active : ''}` }
    onClick={ () => setIsMenuOpen(false) }
  >
    <Link href={ href } variant="centered-block">{ children }</Link>
  </li>

const ItemExternalLink = ({ handleClick, setIsMenuOpen, href, children, isActive }) =>
  <li
    className={ `${styles.item} ${isActive ? styles.active : ''}` }
    onClick={ () => {
      handleClick()
      setIsMenuOpen(false)
    } }
  >
    <ExternalLink href={ href } variant="centered-block">{ children }</ExternalLink>
  </li>

const ItemButton = ({ setIsMenuOpen, onClick, children }) =>
  <li className={ styles.item } onClick={ () => setIsMenuOpen(false) }>
    <Button onClick={ onClick } variant="full">{ children }</Button>
  </li>

Menu.Link = ItemLink
Menu.ExternalLink = ItemExternalLink
Menu.Button = ItemButton

Dropdown.Toggle = Toggle
Dropdown.Menu = Menu

Dropdown.propTypes = {
  locale: PropTypes.oneOf(getLocales()).isRequired,
  children: PropTypes.oneOfType([
    PropTypes.shape({
      type: PropTypes.oneOf([Toggle, Menu]),
    }),
    PropTypes.arrayOf(
      PropTypes.shape({
        type: PropTypes.oneOf([Toggle, Menu]),
      }),
    ),
  ]).isRequired,
}

Dropdown.Toggle.propTypes = {
  children: PropTypes.node.isRequired,
}

Dropdown.Menu.propTypes = {
  children: PropTypes.oneOfType([
    PropTypes.shape({
      type: PropTypes.oneOf([ItemLink, ItemExternalLink, ItemButton]),
    }),
    PropTypes.arrayOf(
      PropTypes.shape({
        type: PropTypes.oneOf([ItemLink, ItemExternalLink, ItemButton]),
      }),
    ),
  ]).isRequired,
}

Dropdown.Menu.Link.propTypes = {
  href: PropTypes.string.isRequired,
  isActive: PropTypes.bool.isRequired,
  children: PropTypes.node.isRequired,
}

Dropdown.Menu.ExternalLink.propTypes = {
  href: PropTypes.string.isRequired,
  handleClick: PropTypes.func.isRequired,
  isActive: PropTypes.bool.isRequired,
  children: PropTypes.node.isRequired,
}

Dropdown.Menu.Button.propTypes = {
  onClick: PropTypes.func.isRequired,
  children: PropTypes.node.isRequired,
}

export default Dropdown
